import React, { useMemo, useRef, useState } from 'react';
import { ArrowDown, ArrowUp, ArrowUpDown, Plus, Search, Trash2 } from 'lucide-react';
import { useUIColors } from '../../hooks/useTheme';

const ROLE_BADGE_TONES = {
  administrador: { bg: 'rgba(15, 118, 110, 0.14)', fg: '#0f766e' },
  director: { bg: 'rgba(30, 64, 175, 0.12)', fg: '#1e40af' },
  gerente: { bg: 'rgba(180, 83, 9, 0.14)', fg: '#9a3412' },
  business_developer: { bg: 'rgba(3, 105, 161, 0.14)', fg: '#0369a1' },
  procurement: { bg: 'rgba(67, 56, 202, 0.12)', fg: '#4338ca' },
  production_manager: { bg: 'rgba(21, 128, 61, 0.14)', fg: '#166534' },
};

const TABLE_COLUMNS = [
  { key: 'Usuario', align: 'left', sortKey: 'full_name' },
  { key: 'Cargo', align: 'left', sortKey: 'job_title' },
  { key: 'Roles', align: 'left', sortKey: 'roles' },
  { key: 'Fono', align: 'left', sortKey: 'fono' },
  { key: 'Estado', align: 'center', sortKey: 'status' },
];

const getInitials = (name = '') => {
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
};

const getRoleTone = (code, interactive) => {
  const key = String(code || '').toLowerCase();
  if (ROLE_BADGE_TONES[key]) return ROLE_BADGE_TONES[key];
  return {
    bg: `color-mix(in srgb, ${interactive} 14%, transparent)`,
    fg: interactive,
  };
};

const getSortValue = (row, sortKey) => {
  switch (sortKey) {
    case 'full_name':
      return String(row.full_name || '').toLowerCase();
    case 'job_title':
      return String(row.job_title || '').toLowerCase();
    case 'roles':
      return (Array.isArray(row.roles) ? row.roles : [])
        .map((r) => r.name || r.code || '')
        .join(' ')
        .toLowerCase();
    case 'fono':
      return String(row.fono || '').toLowerCase();
    case 'status':
      return String(row.status || '').toLowerCase();
    default:
      return '';
  }
};

const compareRows = (a, b, sortKey, sortDir) => {
  const mult = sortDir === 'asc' ? 1 : -1;
  const va = getSortValue(a, sortKey);
  const vb = getSortValue(b, sortKey);
  return String(va).localeCompare(String(vb), 'es', { numeric: true, sensitivity: 'base' }) * mult;
};

/**
 * Tabla de usuarios: mismo look de barra superior / filtros que Table3 (Contactos, Cotizaciones, etc.).
 */
const UsersTable3 = ({
  rows = [],
  loading = false,
  error = '',
  isDarkMode = false,
  tableTitle = 'Usuarios',
  addButtonLabel = 'Agregar usuario',
  onAdd,
  onRowClick,
  onBulkDelete,
}) => {
  const ui = useUIColors();
  const bg = ui.page;
  const headerBg = ui.header;
  const border = ui.border;
  const text = ui.text;
  const muted = ui.muted;
  const rowHover = ui.rowHover;
  const inputBg = ui.inputBg || ui.panel || bg;
  const inputBorder = ui.inputBorder || border;
  const padV = 9;
  const padH = 10;
  const thPadV = 8;

  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterRole, setFilterRole] = useState('');
  const [sortColumn, setSortColumn] = useState('full_name');
  const [sortDir, setSortDir] = useState('asc');
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkPending, setBulkPending] = useState(false);
  const [bulkError, setBulkError] = useState('');
  const headerSelectRef = useRef(null);

  const selectStyle = {
    height: 36,
    padding: '0 10px',
    borderRadius: 8,
    border: `1px solid ${inputBorder}`,
    background: inputBg,
    color: text,
    fontSize: 13,
    outline: 'none',
    boxSizing: 'border-box',
  };

  const roleOptions = useMemo(() => {
    const map = new Map();
    rows.forEach((row) => {
      (Array.isArray(row.roles) ? row.roles : []).forEach((role) => {
        const code = role.code || String(role.id);
        if (!map.has(code)) map.set(code, role.name || code);
      });
    });
    return Array.from(map.entries())
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label, 'es'));
  }, [rows]);

  const statusOptions = useMemo(() => {
    const set = new Set();
    rows.forEach((row) => {
      const st = String(row.status || '').trim();
      if (st) set.add(st);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'es'));
  }, [rows]);

  const filteredRows = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return rows.filter((row) => {
      if (filterStatus && String(row.status || '') !== filterStatus) return false;
      if (filterRole) {
        const codes = (Array.isArray(row.roles) ? row.roles : []).map((r) => r.code || String(r.id));
        if (!codes.includes(filterRole)) return false;
      }
      if (!q) return true;
      const rolesText = (Array.isArray(row.roles) ? row.roles : [])
        .map((r) => `${r.name || ''} ${r.code || ''}`)
        .join(' ');
      const haystack = [
        row.full_name,
        row.email,
        row.job_title,
        row.fono,
        row.user_code,
        row.status,
        rolesText,
      ]
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [rows, searchQuery, filterStatus, filterRole]);

  const sortedRows = useMemo(() => {
    const copy = [...filteredRows];
    copy.sort((a, b) => compareRows(a, b, sortColumn, sortDir));
    return copy;
  }, [filteredRows, sortColumn, sortDir]);

  const visibleIds = sortedRows.map((r) => r.id);
  const allVisibleSelected =
    visibleIds.length > 0 && visibleIds.every((id) => selectedIds.includes(id));
  const someVisibleSelected = visibleIds.some((id) => selectedIds.includes(id));

  React.useEffect(() => {
    if (headerSelectRef.current) {
      headerSelectRef.current.indeterminate = someVisibleSelected && !allVisibleSelected;
    }
  }, [someVisibleSelected, allVisibleSelected]);

  const handleSortClick = (sortKey) => {
    if (sortColumn === sortKey) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(sortKey);
      setSortDir('asc');
    }
  };

  const SortIcon = ({ columnKey }) => {
    if (sortColumn !== columnKey) return <ArrowUpDown size={12} color={muted} />;
    return sortDir === 'asc' ? <ArrowUp size={12} color={text} /> : <ArrowDown size={12} color={text} />;
  };

  const clearFilters = () => {
    setSearchQuery('');
    setFilterStatus('');
    setFilterRole('');
  };

  const hasActiveFilters =
    Boolean(searchQuery.trim()) || Boolean(filterStatus) || Boolean(filterRole);

  const toggleSelectAllVisible = () => {
    if (allVisibleSelected) {
      setSelectedIds((prev) => prev.filter((id) => !visibleIds.includes(id)));
    } else {
      setSelectedIds((prev) => Array.from(new Set([...prev, ...visibleIds])));
    }
  };

  const toggleRow = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleBulkDelete = async () => {
    if (!onBulkDelete || selectedIds.length === 0) return;
    const confirmMessage =
      selectedIds.length === 1
        ? '¿Eliminar este usuario?'
        : `¿Eliminar ${selectedIds.length} usuarios?`;
    if (!window.confirm(confirmMessage)) return;
    setBulkPending(true);
    setBulkError('');
    try {
      const selectedRows = rows.filter((r) => selectedIds.includes(r.id));
      await onBulkDelete(selectedRows);
      setSelectedIds([]);
    } catch (err) {
      setBulkError(err.message || 'No se pudo eliminar.');
    } finally {
      setBulkPending(false);
    }
  };

  const activeCount = rows.filter(
    (u) => String(u.status || '').toLowerCase() === 'activo'
  ).length;
  const subtitle = loading
    ? 'Cargando datos...'
    : error ||
      `${rows.length} usuario${rows.length === 1 ? '' : 's'} · ${activeCount} activo${activeCount === 1 ? '' : 's'}`;

  const showBulkChrome = typeof onBulkDelete === 'function';

  return (
    <div
      style={{
        border: `1px solid ${border}`,
        borderRadius: 12,
        overflow: 'hidden',
        background: bg,
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '12px 16px',
          borderBottom: `1px solid ${border}`,
          background: headerBg,
          flexWrap: 'wrap',
          gap: 10,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap', minWidth: 0 }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: text }}>{tableTitle}</h3>
          <p style={{ margin: 0, fontSize: 12, color: muted }}>{subtitle}</p>
        </div>
        {onAdd ? (
          <button
            type="button"
            onClick={onAdd}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              height: 36,
              padding: '0 14px',
              borderRadius: 8,
              border: 'none',
              background: '#16a34a',
              color: '#fff',
              fontWeight: 600,
              fontSize: 13,
              cursor: 'pointer',
            }}
          >
            <Plus size={16} /> {addButtonLabel}
          </button>
        ) : null}
      </div>

      <div
        style={{
          padding: '10px 16px',
          borderBottom: `1px solid ${border}`,
          background: bg,
          display: 'flex',
          flexWrap: 'wrap',
          gap: 10,
          alignItems: 'flex-end',
        }}
      >
        <label style={{ display: 'flex', flexDirection: 'column', gap: 6, flex: '1 1 220px' }}>
          <span style={{ fontSize: 11, fontWeight: 600, color: muted, letterSpacing: '0.04em' }}>
            Buscar
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Search size={16} color={muted} aria-hidden />
            <input
              type="search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Nombre, email, cargo, rol…"
              style={{ ...selectStyle, flex: 1, minWidth: 0, cursor: 'text' }}
            />
          </span>
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 140 }}>
          <span style={{ fontSize: 11, fontWeight: 600, color: muted, letterSpacing: '0.04em' }}>
            Estado
          </span>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            style={selectStyle}
          >
            <option value="">Todos</option>
            {statusOptions.map((st) => (
              <option key={st} value={st}>
                {st}
              </option>
            ))}
          </select>
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 180 }}>
          <span style={{ fontSize: 11, fontWeight: 600, color: muted, letterSpacing: '0.04em' }}>
            Rol de sistema
          </span>
          <select
            value={filterRole}
            onChange={(e) => setFilterRole(e.target.value)}
            style={selectStyle}
          >
            <option value="">Todos</option>
            {roleOptions.map((role) => (
              <option key={role.value} value={role.value}>
                {role.label}
              </option>
            ))}
          </select>
        </label>
        {hasActiveFilters ? (
          <button
            type="button"
            onClick={clearFilters}
            style={{
              height: 36,
              padding: '0 12px',
              borderRadius: 8,
              border: `1px solid ${inputBorder}`,
              background: inputBg,
              color: text,
              fontSize: 13,
              cursor: 'pointer',
            }}
          >
            Limpiar
          </button>
        ) : null}
      </div>

      {showBulkChrome && selectedIds.length > 0 ? (
        <div
          style={{
            padding: '10px 16px',
            borderBottom: `1px solid ${border}`,
            background: isDarkMode ? '#1c1917' : '#fafaf9',
            display: 'flex',
            flexWrap: 'wrap',
            gap: 10,
            alignItems: 'center',
          }}
        >
          <span style={{ fontSize: 13, color: text, fontWeight: 600 }}>
            {selectedIds.length} seleccionado{selectedIds.length === 1 ? '' : 's'}
          </span>
          <button
            type="button"
            disabled={bulkPending}
            onClick={handleBulkDelete}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              height: 32,
              padding: '0 12px',
              borderRadius: 6,
              border: 'none',
              background: '#dc2626',
              color: '#fff',
              fontWeight: 600,
              fontSize: 12,
              cursor: bulkPending ? 'wait' : 'pointer',
              opacity: bulkPending ? 0.7 : 1,
            }}
          >
            <Trash2 size={14} /> Eliminar
          </button>
          {bulkError ? <span style={{ fontSize: 12, color: '#dc2626' }}>{bulkError}</span> : null}
        </div>
      ) : null}

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 780 }}>
          <thead>
            <tr style={{ background: headerBg, borderBottom: `1px solid ${border}` }}>
              {showBulkChrome ? (
                <th style={{ padding: `${thPadV}px ${padH}px`, width: 36, textAlign: 'center' }}>
                  <input
                    ref={headerSelectRef}
                    type="checkbox"
                    checked={allVisibleSelected}
                    onChange={toggleSelectAllVisible}
                    aria-label="Seleccionar todos"
                  />
                </th>
              ) : null}
              {TABLE_COLUMNS.map((col) => (
                <th
                  key={col.key}
                  style={{
                    padding: `${thPadV}px ${padH}px`,
                    textAlign: col.align,
                    fontSize: 11,
                    fontWeight: 700,
                    color: muted,
                    letterSpacing: '0.04em',
                    textTransform: 'uppercase',
                    whiteSpace: 'nowrap',
                    cursor: 'pointer',
                    userSelect: 'none',
                  }}
                  onClick={() => handleSortClick(col.sortKey)}
                >
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    {col.key}
                    <SortIcon columnKey={col.sortKey} />
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td
                  colSpan={TABLE_COLUMNS.length + (showBulkChrome ? 1 : 0)}
                  style={{ padding: 24, textAlign: 'center', color: muted, fontSize: 13 }}
                >
                  Cargando usuarios…
                </td>
              </tr>
            ) : sortedRows.length === 0 ? (
              <tr>
                <td
                  colSpan={TABLE_COLUMNS.length + (showBulkChrome ? 1 : 0)}
                  style={{ padding: 24, textAlign: 'center', color: muted, fontSize: 13 }}
                >
                  {error || 'No hay usuarios para mostrar.'}
                </td>
              </tr>
            ) : (
              sortedRows.map((row) => {
                const selected = selectedIds.includes(row.id);
                const roles = Array.isArray(row.roles) ? row.roles : [];
                const active = String(row.status || '').toLowerCase() === 'activo';
                return (
                  <tr
                    key={row.id}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = rowHover || headerBg;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = selected
                        ? `color-mix(in srgb, ${ui.interactive} 8%, ${bg})`
                        : bg;
                    }}
                    style={{
                      borderBottom: `1px solid ${border}`,
                      background: selected
                        ? `color-mix(in srgb, ${ui.interactive} 8%, ${bg})`
                        : bg,
                      cursor: onRowClick ? 'pointer' : 'default',
                    }}
                    onClick={() => onRowClick?.(row)}
                  >
                    {showBulkChrome ? (
                      <td
                        style={{ padding: `${padV}px ${padH}px`, textAlign: 'center' }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <input
                          type="checkbox"
                          checked={selected}
                          onChange={() => toggleRow(row.id)}
                          aria-label={`Seleccionar ${row.full_name || row.id}`}
                        />
                      </td>
                    ) : null}
                    <td style={{ padding: `${padV}px ${padH}px` }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                        <span
                          aria-hidden
                          style={{
                            width: 34,
                            height: 34,
                            borderRadius: 10,
                            flexShrink: 0,
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: 12,
                            fontWeight: 700,
                            background: `color-mix(in srgb, ${ui.interactive} 14%, ${bg})`,
                            color: ui.interactive,
                            border: `1px solid color-mix(in srgb, ${ui.interactive} 22%, ${border})`,
                          }}
                        >
                          {getInitials(row.full_name)}
                        </span>
                        <div style={{ minWidth: 0, lineHeight: 1.25 }}>
                          <div style={{ fontWeight: 700, fontSize: 12, color: text }}>
                            {row.full_name || '—'}
                          </div>
                          <div style={{ fontSize: 11, color: muted, marginTop: 1 }}>
                            {row.email || 'Sin correo'}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td style={{ padding: `${padV}px ${padH}px`, fontSize: 12, color: text }}>
                      {row.job_title || '—'}
                    </td>
                    <td style={{ padding: `${padV}px ${padH}px` }}>
                      {roles.length === 0 ? (
                        <span style={{ fontSize: 12, color: muted, fontStyle: 'italic' }}>
                          Sin rol
                        </span>
                      ) : (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                          {roles.map((role) => {
                            const tone = getRoleTone(role.code, ui.interactive);
                            return (
                              <span
                                key={role.id || role.code}
                                title={role.code}
                                style={{
                                  backgroundColor: tone.bg,
                                  color: tone.fg,
                                  padding: '3px 8px',
                                  borderRadius: 6,
                                  fontSize: 11,
                                  fontWeight: 650,
                                  border: `1px solid color-mix(in srgb, ${tone.fg} 18%, transparent)`,
                                }}
                              >
                                {role.name || role.code}
                              </span>
                            );
                          })}
                        </div>
                      )}
                    </td>
                    <td style={{ padding: `${padV}px ${padH}px`, fontSize: 12, color: row.fono ? text : muted }}>
                      {row.fono || '—'}
                    </td>
                    <td style={{ padding: `${padV}px ${padH}px`, textAlign: 'center' }}>
                      <span
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 6,
                          padding: '3px 9px',
                          borderRadius: 999,
                          fontSize: 11,
                          fontWeight: 650,
                          color: active ? '#166534' : muted,
                          background: active
                            ? 'rgba(22, 101, 52, 0.12)'
                            : `color-mix(in srgb, ${muted} 14%, transparent)`,
                        }}
                      >
                        <span
                          style={{
                            width: 6,
                            height: 6,
                            borderRadius: '50%',
                            background: active ? '#16a34a' : muted,
                          }}
                        />
                        {row.status || '—'}
                      </span>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default UsersTable3;
